/* BD HERBS — header (sections/bd-header.liquid): phone drawer, phone search, compact sticky header. */
if (!customElements.get('bd-header')) {
  customElements.define(
    'bd-header',
    class BdHeader extends HTMLElement {
      constructor() {
        super();
        this.onKey = this.onKey.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.closeAll = () => {
          this.closeDrawer(false);
          this.closeSearch(false);
        };
      }

      connectedCallback() {
        this.section = this.closest('.shopify-section') || this;
        document.addEventListener('bd:close-drawers', this.closeAll);
        document.addEventListener('keydown', this.onKey);
        if (this.dataset.compact === 'true') {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.onScroll();
        }

        // Element listeners only once, even if the header is moved and reconnected
        if (this.bound) return;
        this.bound = true;
        this.drawer = this.querySelector('[data-hd-drawer]');
        this.drawerPanel = this.drawer && this.drawer.querySelector('.bd-drawer__panel');
        this.drawerButton = this.querySelector('[data-hd-drawer-open]');
        this.search = this.querySelector('[data-hd-search]');
        this.searchButton = this.querySelector('[data-hd-search-toggle]');
        this.searchInput = this.search && this.search.querySelector('input[type="search"]');

        this.drawerButton?.addEventListener('click', () => this.openDrawer());
        this.querySelectorAll('[data-hd-drawer-close]').forEach((el) => el.addEventListener('click', () => this.closeDrawer()));
        this.searchButton?.addEventListener('click', () => (this.classList.contains('is-search-open') ? this.closeSearch() : this.openSearch()));

        // Bottom bar "সার্চ" item
        this.querySelectorAll('[data-hd-search-open]').forEach((el) =>
          el.addEventListener('click', (event) => {
            event.preventDefault();
            window.scrollTo({ top: 0, behavior: 'auto' });
            this.openSearch();
          })
        );
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKey);
        document.removeEventListener('bd:close-drawers', this.closeAll);
        window.removeEventListener('scroll', this.onScroll);
        document.body.classList.remove('overflow-hidden');
      }

      /* ---------- Drawer ---------- */
      openDrawer() {
        if (!this.drawer) return;
        this.closeSearch(false);
        this.drawer.hidden = false;
        requestAnimationFrame(() => this.drawer.classList.add('is-open'));
        this.drawerButton?.setAttribute('aria-expanded', 'true');
        document.body.classList.add('overflow-hidden');
        this.drawerPanel?.focus({ preventScroll: true });
      }

      closeDrawer(returnFocus = true) {
        if (!this.drawer || this.drawer.hidden) return;
        this.drawer.classList.remove('is-open');
        this.drawerButton?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('overflow-hidden');
        const done = () => {
          this.drawer.hidden = true;
        };
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
        else setTimeout(done, 250);
        if (returnFocus) this.drawerButton?.focus({ preventScroll: true });
      }

      // Keep Tab inside the open drawer
      trapFocus(event) {
        const focusable = [...this.drawerPanel.querySelectorAll('a[href], button, summary, [tabindex]:not([tabindex="-1"])')].filter(
          (el) => el.offsetParent !== null
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === this.drawerPanel)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      /* ---------- Phone search ---------- */
      openSearch() {
        if (!this.search) return;
        this.classList.add('is-search-open');
        this.searchButton?.setAttribute('aria-expanded', 'true');
        requestAnimationFrame(() => this.searchInput?.focus());
      }

      closeSearch(returnFocus = true) {
        if (!this.classList.contains('is-search-open')) return;
        this.classList.remove('is-search-open');
        this.searchButton?.setAttribute('aria-expanded', 'false');
        if (returnFocus) this.searchButton?.focus({ preventScroll: true });
      }

      onKey(event) {
        const drawerOpen = this.drawer && !this.drawer.hidden;
        if (event.key === 'Escape') {
          if (drawerOpen) this.closeDrawer();
          else this.closeSearch();
        } else if (event.key === 'Tab' && drawerOpen) {
          this.trapFocus(event);
        }
      }

      /* ---------- Compact on scroll: menu row folds away, row 1 gets smaller ---------- */
      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          const y = window.scrollY;
          const compact = this.section.classList.contains('is-compact');
          // A little hysteresis so it doesn't flicker around the threshold
          if (!compact && y > 120) this.section.classList.add('is-compact');
          else if (compact && y < 40) this.section.classList.remove('is-compact');
        });
      }
    }
  );
}

/* ---------- Account popup: Shopify's <shopify-account> (snippets/bd-account.liquid) ---------- */
if (!window.bdAccountPopup) {
  window.bdAccountPopup = true;

  // Shopify draws the sheet itself; we only choose where its top edge sits.
  // Header icon: just under the icon. Phone bottom bar: a panel resting on top of the bar.
  const place = (el) => {
    const bar = el.closest('.bd-bnav');
    if (!bar) {
      const bottom = Math.max(0, Math.round(el.getBoundingClientRect().bottom + 6));
      el.style.setProperty('--bd-acct-top', `${bottom}px`);
      return;
    }

    const sheet = el.shadowRoot && el.shadowRoot.querySelector('dialog, [role="dialog"]');
    if (!sheet) {
      // Can't measure the sheet: open it under the phone header instead
      const header = document.querySelector('.bd-hd__bar');
      const below = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      el.style.setProperty('--bd-acct-top', `${below}px`);
      return;
    }
    const barTop = bar.getBoundingClientRect().top;
    const top = Math.max(8, Math.round(barTop - sheet.getBoundingClientRect().height - 8));
    el.style.setProperty('--bd-acct-top', `${top}px`);
  };

  // The `open` event is composed but doesn't bubble, so listen during capture.
  document.addEventListener(
    'open',
    (event) => {
      const el = event.target;
      if (!(el instanceof HTMLElement) || !el.matches('shopify-account[data-bd-acct]')) return;
      // Phone bar: start below the screen, then lift the panel once its height is known
      if (el.closest('.bd-bnav')) el.style.setProperty('--bd-acct-top', `${window.innerHeight}px`);
      else place(el);
      requestAnimationFrame(() => requestAnimationFrame(() => place(el)));
    },
    true
  );

  // Tapping the label under the icon opens the popup too (works when the component's shadow root is open)
  document.addEventListener('click', (event) => {
    const label = event.target instanceof Element && event.target.closest('.bd-acct__label');
    if (!label) return;
    const el = label.parentElement.querySelector('shopify-account[data-bd-acct]');
    const button = el && el.shadowRoot && el.shadowRoot.querySelector('button');
    if (button) button.click();
    else if (el && !customElements.get('shopify-account')) {
      const fallback = label.parentElement.querySelector('.bd-acct__fallback');
      if (fallback) window.location.href = fallback.href;
    }
  });
}
